import { type DateFormats, formatDate, formatDateTime, formatTime } from './dateFormat';

/** Small display formatters — pure, tested. */

/**
 * `zone` is the tenant's dispatch timezone (null renders in the viewer's own)
 * and `formats` its date/time patterns from dispatch's System Configs.
 * Components get both bound via `useTimeFormat()` rather than passing them here.
 */
export function fmtDateTime(
  iso: string | null,
  zone: string | null | undefined,
  formats: DateFormats,
): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return formatDateTime(date, zone, formats);
}

/**
 * A zone-LESS wall time (`YYYY-MM-DDTHH:mm`, what a datetime-local input holds)
 * rendered exactly as written. It is never converted — the value already means
 * a clock reading in some other zone, so shifting it into the display zone
 * would move an agent-entered pickup by hours.
 *
 * The tenant's own patterns still apply to the SHAPE: the components are pinned
 * into UTC and rendered there, so the formatter prints the digits it was given
 * while a tenant on `MM/dd/yyyy` still reads a pickup the way they read
 * everything else.
 */
export function fmtWallTime(wall: string, formats: DateFormats): string {
  const parsed = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(wall);
  if (!parsed) return wall;
  const [, year, month, day, hour, minute] = parsed;
  // Built AND formatted in UTC, so no zone arithmetic can touch it.
  const at = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute)),
  );
  return `${formatDate(at, 'UTC', formats.dateFormat)}, ${formatTime(at, 'UTC', formats.timeFormat)}`;
}

export function fmtDurationMs(ms: number | null): string {
  if (ms === null || ms < 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${Math.round(seconds % 60)}s`;
}

export function fmtTokens(input: number | null, output: number | null): string {
  if (input === null && output === null) return '—';
  const compact = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);
  return `${compact(input ?? 0)} in · ${compact(output ?? 0)} out`;
}
